import React, { useCallback, useContext, useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormInput } from "../../components/FormInput";
import { Button } from "primereact/button";
import { checkLoading, SystemLoading } from "../../store/loadingStore";
import { F2010102Before, downloadAuthorizationLetter, getCompanyData, updateCompanyData } from "../../utility/service/dataRegister-service";
import { AlertService } from "../../utility/service/alert-service";
import { checkDpSuccess, getCompanyType, getFormErrorMessage } from "../../utility/service/tool-service";
import { MessageContext, handleSuccessMessage } from "../../store/messageStore";
import { Dropdown } from "primereact/dropdown";
import { classNames } from "primereact/utils";
import { queryIndustryList, queryLocaleList } from "../../utility/service/register-service";
import { addFormValidator } from "../../utility/validate/formValidator";
import { FormTextarea } from "../../components/FormTextarea";
import { queryCompanyList } from "../../utility/service/company-service";
import { MaskedFormInput } from "../../components/MaskedFormInput";
import { updateAuditEyesEvent } from "../../utility/service/audilog-service";
import { useFuncId } from "../../router/menuRouterMapper";
import StorageProgressBar from "../../components/StorageProgressBar";
import { getCBAMSubItemsByItemNo } from "../../utility/service/companyOffering-service";
import { useEditableFlag } from "../../hooks/useEditableFlag";

const CompanyDataSetting = () => {
  const { t } = useTranslation();
  const [readonly, setReadonly] = useState(true);
  const [, loading] = useContext(SystemLoading);
  const [, dispatch] = useContext(MessageContext); //訊息視窗
  const [companyInfo, setCompanyInfo] = useState({});
  const [industryList, setIndustryList] = useState([]);
  const [formRule, setFormRule] = useState([]); //表單驗證
  const [companyPlaceList, setCompanyPlaceList] = useState([]);
  const [maskView, setMaskView] = useState(false);
  const chsFuncId = useFuncId();
  const [localeList, setLocaleList] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const isEditable = useEditableFlag('F10403'); // Get editable status for F10403 function

  const {
    register,
    handleSubmit,
    watch,
    clearErrors,
    getValues,
    setValue,
    resetField,
    reset: resetForm,
    trigger,
    setError,
    control,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm({
    defaultValues: {
      taxIdNum: "",
      name: "",
      address: "",
      leader: "",
      email: "",
    },
    //錯誤驗證時機
    mode: "all",
  });

  const {
    fields: placeFields,
    append: addForm,
    prepend,
    remove: delForm,
    move,
    insert: insertForm,
    update,
  } = useFieldArray({
    control,
    name: "placeList",
  });

  const onSubmit = (data) => {
    // console.log(data);
    setReadonly(true);
    setValue("placeList", data?.placeList);
    let reqBody = {
      name: data.name,
      address: data.address,
      leader: data.leader,
      email: data.email,
      industryCode: data.industryCode,
      companySeq: data.companySeq,
      history: data.history,
      business: data.business,
      policy: data.policy,
      locale: data.locale,
      country: data.country,
    };

    if (companyInfo?.isConsultantCompany === "Y") {
      //若為顧問帳戶則回傳空List
      reqBody.placesName = [];
    } else {
      reqBody.placeName = companyInfo?.places?.map((item) => item?.placeName);
    }

    updateCompanyData(reqBody).then((res) => {
      if (checkDpSuccess(res)) {
        handleSuccessMessage(dispatch, t("button.update") + " " + t("message.success"));
        setMaskView(false);
      }
    });
  };

  const axios_getCompanyData = (reqBody) => {
    checkLoading(loading);
    getCompanyData(reqBody)
      .then((res) => {
        if (checkDpSuccess(res)) {
          let data = res.data.RespBody;
          setCompanyInfo(data);
          setValue("taxIdNum", data.taxIdNum);
          setValue("name", data.name);
          setValue("address", data.address);
          setValue("email", data.email);
          setValue("leader", data?.leader);
          setValue("placeList", data?.placeList);
          setValue("industryCode", data?.industryCode);
          setValue("history", data?.history);
          setValue("business", data?.business);
          setValue("policy", data?.policy);
          setValue("locale", data?.locale, { shouldValidate: true });
          setValue("useS3Bucket", data?.useS3Bucket);
          setValue("s3BucketQuota", data?.s3BucketQuota);
          setValue("country", data?.country);
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  //取得行業別下拉清單
  const axios_queryIndustryList = () => {
    queryIndustryList().then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody?.industryList;
        setIndustryList(data);
      }
    });
  };

  //組織邊界廠商清單
  const axios_queryCompanyList = () => {
    queryCompanyList()
      .then((res) => {
        if (checkDpSuccess(res)) {
          let data = res.data.RespBody.companyList;
          setCompanyPlaceList(data);
          if (getCompanyType() === "A") {
            setValue("companySeq", data[0].companySeq);
            trigger("companySeq");
            axios_getCompanyData({ companySeq: data[0].companySeq });
          }
        }
      })
      .finally((fin) => {});
  };

  // 國家下拉選單
  const axios_queryCBAMSubItemsByItemNo = (itemNo, orderNo) => {
    return getCBAMSubItemsByItemNo(itemNo, orderNo).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.subItems;
        return data.map((item, index) => ({
          ...item,
          index,
        }));
      }
      return [];
    });
  };

  useEffect(() => {
    axios_queryIndustryList(); //取得行業別清單
    axios_queryLocaleDropdown(); //locale list
    axios_queryCBAMSubItemsByItemNo("CBAM_COUNTRIES", 0).then(setCountryList);
    axios_getCreateBefore(); //統一檢核
    axios_queryCompanyList();
    // setValue("placeList", companyInfo?.placeList);
    //   checkLoading(loading);
    //   setTimeout(() => {
    //     checkLoading(loading, false);
    //   }, 1000)
    //   isPlaceNameRepeat(placeFields, "高軟廠測區")
  }, []);

  //建立前查詢form 規則
  const axios_getCreateBefore = () => {
    F2010102Before().then((res) => {
      if (checkDpSuccess(res)) {
        let array = [];
        res?.data?.RespBody?.constraints?.forEach((item) => {
          const result = addFormValidator(item?.field, res.data.RespBody.constraints);
          array.push(result);
        });
        setFormRule(array);
      }
    });
  };

  const setValidForm = useCallback(
    (formId) => {
      if (formRule?.length > 0) {
        const tmp = formRule?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [formRule]
  );

  const handleResetForm = () => {
    resetForm({ placeList: [{ placeName: "", isUsed: "N" }] });
    clearErrors();
  };

  const handleBtnCancel = () => {
    clearErrors(["name", "leader", "email"]);
    axios_getCompanyData({ companySeq: getValues("companySeq") });
  };

  //判斷是否輸入的places 中的 placeName 有重複值出現
  const isPlaceNameRepeat = (array, placeName) => {
    // array?.map((item,index)=>item?.placeName === placeName)
    console.log(array?.map((item, index) => item?.placeName)?.filter((place) => place === placeName));
  };

  const checkKeyDown = (e) => {
    // if (e.key === 'Enter') e.preventDefault();
    if (e.keyCode === 13) {
      e.stopPropagation();
    }
  };

  const inputCheckKeyDown = (e) => {
    if (e.key === "Enter") e.preventDefault();
  };

  const handleMaskClick = (index) => {
    setMaskView((prev) => {
      return !prev;
    });
  };

  const axios_updateAuditEyesEvent = () => {
    updateAuditEyesEvent({
      functionId: chsFuncId,
      eventType: "E",
    }).then((res) => {
      if (checkDpSuccess(res)) {
      }
    });
  };

  const parseYMDToUTCDate = (ymd) => {
    if (typeof ymd !== "string") return null;
    const match = ymd.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
    if (!match) return null;
    const y = Number(match[1]);
    const m = Number(match[2]);
    const d = Number(match[3]);
    return new Date(Date.UTC(y, m - 1, d));
  };

  // 下載授權書只能在「正式會員」且「授權日期有效」時顯示且可點擊
  const canDownloadAuthorizationLetter = () => {
    const effectiveType = companyInfo?.effectiveType;
    const startStr = companyInfo?.effectiveStartDate;
    const endStr = companyInfo?.effectiveEndDate;

    // 正式會員（ZH/EN/VI 皆兼容；後端 effectiveType 會做語系轉換）
    const isOfficialMember =
      effectiveType === "正式" ||
      (typeof effectiveType === "string" && effectiveType.includes("正式")) ||
      effectiveType === "Full Version" ||
      (typeof effectiveType === "string" && effectiveType.includes("Full")) ||
      (typeof effectiveType === "string" && effectiveType.includes("Phiên"));

    const startDate = parseYMDToUTCDate(startStr);
    if (!isOfficialMember || !startDate) return false;

    const endDate = endStr ? parseYMDToUTCDate(endStr) : null;
    if (endStr && !endDate) return false;

    const today = new Date();
    const todayUTC = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
    if (todayUTC < startDate) return false;

    // endDate 為空時視為開放區間
    return !endDate || todayUTC <= endDate;
  };

  const openAuthorizationLetterPrintPreview = (htmlContent) => {
    const existing = document.getElementById("print-preview-frame");
    if (existing) {
      existing.remove();
    }

    const iframe = document.createElement("iframe");
    iframe.id = "print-preview-frame";
    iframe.style.position = "absolute";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);

    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (error) {
        console.error("Print failed:", error);
      }
    };

    iframe.contentWindow.document.open();
    iframe.contentWindow.document.write(htmlContent);
    iframe.contentWindow.document.close();
  };

  const handleDownloadAuthorizationLetter = () => {
    const companySeq = getValues("companySeq");
    if (!companySeq) {
      return;
    }
    checkLoading(loading);
    downloadAuthorizationLetter({ companySeq })
      .then((res) => {
        if (res?.status === 200 && res.data) {
          if (typeof res.data === "string") {
            try {
              const jsonData = JSON.parse(res.data);
              AlertService.ok(jsonData?.ResHeader?.rtnMsg, "", "warning", jsonData?.ResHeader?.txId);
            } catch (error) {
              openAuthorizationLetterPrintPreview(res.data);
            }
          }
        } else if (res?.data?.ResHeader) {
          AlertService.ok(res.data.ResHeader.rtnCode, res.data.ResHeader.rtnMsg, "warning", res.data.ResHeader.txId);
        }
      })
      .catch((error) => {
        console.error("Error:", error);
      })
      .finally(() => checkLoading(loading, false));
  };

  const axios_queryLocaleDropdown = () => {
    queryLocaleList().then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody?.localeList;
        setLocaleList(data);
      }
    });
  };

  return (
    <div className="p-card">
      <div className="p-card p-card-body">
        <form onSubmit={handleSubmit(onSubmit)} onKeyDown={checkKeyDown} className="grid">
          {/* 組織名稱 */}
          <div className="col-12 lg:col-12">
            <label htmlFor="placeName" className={`form-label ${readonly === false ? "required" : ""}`}>
              {t("organName")}
            </label>
            <Controller
              name="companySeq"
              control={control}
              rules={{ required: { value: true, message: t("validation.required") } }}
              render={({ field, fieldState }) => (
                <Dropdown
                  filter
                  disabled={getCompanyType() === "A"}
                  appendTo={"self"}
                  optionLabel="companyName"
                  optionValue="companySeq"
                  value={field.value}
                  virtualScrollerOptions={{ itemSize: 40 }}
                  defaultValue={""}
                  placeholder={t("plz_chs")}
                  name="companySeq"
                  control={control}
                  options={companyPlaceList}
                  className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                  onChange={(event) => {
                    field.onChange(event.value);
                    axios_getCompanyData({ companySeq: event.value });
                  }}
                />
              )}
            />
            {getFormErrorMessage(errors, "companySeq")}
          </div>
          {/* 統一編號 */}
          <div className="col-12 lg:col-6">
            <FormInput
              register={register}
              errors={errors}
              id="taxIdNum"
              name="taxIdNum"
              type="text"
              labelText={t("company_num")}
              labelClass="control-label"
              disabled={true}
              enterClick={inputCheckKeyDown}
            />
          </div>
          {/* 組織名稱 */}
          <div className="col-12 lg:col-6">
            <FormInput
              register={register}
              errors={errors}
              id="name"
              name="name"
              type="text"
              labelText={t("organName")}
              labelClass={`${readonly === false ? "required" : ""}`}
              disabled={readonly || !isEditable}
              rules={readonly === false ? setValidForm("name") : ""}
              enterClick={inputCheckKeyDown}
            />
          </div>
          {/* 公司地址 address */}
          <div className="col-12 lg:col-6">
            {/* <FormInput
              register={register}
              errors={errors}
              id="address"
              name="address"
              type="text"
              labelText={t('address')}
              disabled={readonly || !isEditable}
              rules={readonly===false?setValidForm("address"):''}
              enterClick={inputCheckKeyDown}
            /> */}
            <Controller
              name="address"
              control={control}
              rules={readonly === false ? setValidForm("address") : ""}
              render={({ field, fieldState }) => (
                <MaskedFormInput
                  {...field}
                  type="text"
                  labelClass={`${readonly === false ? "required" : ""}`}
                  labelText={t("address")}
                  maskType="address"
                  maskView={maskView}
                  setMaskView={() => handleMaskClick()}
                  disabled={readonly || !isEditable}
                  errorMessage={fieldState.error?.message}
                  onKeyDown={inputCheckKeyDown}
                />
              )}
            />
          </div>
          {/* 公司負責人 leader */}
          <div className="col-12 lg:col-6">
            {/* <FormInput
              labelClass={`${readonly === false ? 'required' : ''}`}
              register={register}
              errors={errors}
              id="leader"
              name="leader"
              type="text"
              labelText={t('companyLeader')}
              disabled={readonly || !isEditable}
              rules={readonly===false?setValidForm("leader"):''}
              enterClick={inputCheckKeyDown}
            /> */}
            <Controller
              name="leader"
              control={control}
              rules={readonly === false ? setValidForm("leader") : ""}
              render={({ field, fieldState }) => (
                <MaskedFormInput
                  {...field}
                  type="text"
                  labelClass={`${readonly === false ? "required" : ""}`}
                  labelText={t("companyLeader")}
                  maskType="name"
                  maskView={maskView}
                  setMaskView={() => handleMaskClick()}
                  disabled={readonly || !isEditable}
                  errorMessage={fieldState.error?.message}
                  onKeyDown={inputCheckKeyDown}
                />
              )}
            />
          </div>
          {/* 公司Eamil */}
          <div className="col-12 lg:col-6">
            {/* <FormInput
              labelClass={`${readonly === false ? 'required' : ''}`}
              register={register}
              errors={errors}
              id="email"
              name="email"
              type="text"
              labelText={t('companyEamil')}
              disabled={readonly || !isEditable}
              rules={readonly===false?setValidForm("email"):''}
              enterClick={inputCheckKeyDown}
            /> */}
            <Controller
              name="email"
              control={control}
              rules={readonly === false ? setValidForm("email") : ""}
              render={({ field, fieldState }) => (
                <MaskedFormInput
                  {...field}
                  type="text"
                  labelClass={`${readonly === false ? "required" : ""}`}
                  labelText={t("companyEamil")}
                  maskType="email"
                  maskView={maskView}
                  setMaskView={() => handleMaskClick()}
                  disabled={readonly || !isEditable}
                  errorMessage={fieldState.error?.message}
                  onKeyDown={inputCheckKeyDown}
                />
              )}
            />
          </div>
          {/* 行業別 */}
          <div className="col-12 lg:col-6">
            <label htmlFor="industryCode" className={`form-label ${readonly === false ? "required" : ""}`}>
              {t("industry")}
            </label>
            <Controller
              name="industryCode"
              control={control}
              rules={readonly === false ? setValidForm("industryCode") : ""}
              render={({ field, fieldState }) => (
                <Dropdown
                  filter
                  disabled={readonly || !isEditable}
                  appendTo={"self"}
                  optionLabel="industryName"
                  optionValue="industryCode"
                  value={field.value}
                  virtualScrollerOptions={{ itemSize: 40 }}
                  defaultValue={""}
                  placeholder={t("plz_chs")}
                  name="industryCode"
                  control={control}
                  options={industryList}
                  className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                  onChange={(event) => {
                    field.onChange(event.value);
                  }}
                />
              )}
            />
            {getFormErrorMessage(errors, "industryCode")}
          </div>
          {/* 授權類型 */}
          <div className="col-12 lg:col-6">
            <label htmlFor="industryCode" className="form-label">
              {t("authorizationType")}
            </label>
            <span>{companyInfo?.effectiveType}</span>
          </div>
          {/* 授權起迄 */}
          <div className="col-12 lg:col-6">
            <label htmlFor="industryCode" className="form-label">
              {t("authorizationDate")}
            </label>
            <span>
              {companyInfo?.effectiveStartDate}{" "}
              {companyInfo?.effectiveEndDate !== undefined ? ` ~ ${companyInfo?.effectiveEndDate}` : ""}
            </span>
          </div>
          {/* 國家 */}
          <div className="col-12 lg:col-6">
            <label className={`form-label ${readonly === false ? "required" : ""}`}>{t("country")}</label>
            <Controller
              name="country"
              control={control}
              rules={setValidForm("country")}
              render={({ field, fieldState }) => (
                <Dropdown
                  filter
                  disabled={readonly || !isEditable}
                  optionLabel="subitemName"
                  optionValue="subitemNo"
                  value={field.value}
                  defaultValue={""}
                  virtualScrollerOptions={{ itemSize: 40 }}
                  placeholder={t("plz_chs")}
                  options={countryList}
                  className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                  onChange={(event) => {
                    field.onChange(event.value);
                  }}
                />
              )}
            />
            {getFormErrorMessage(errors, "country")}
          </div>
          {/* 使用語言 */}
          <div className="col-12 lg:col-6">
            <label className={`form-label ${readonly === false ? "required" : ""}`}>{t("useLanguage")}</label>
            <Controller
              name="locale"
              control={control}
              rules={setValidForm("locale")}
              render={({ field, fieldState }) => (
                <Dropdown
                  disabled={readonly || !isEditable}
                  optionLabel="localeName"
                  optionValue="locale"
                  value={field.value}
                  virtualScrollerOptions={{ itemSize: 40 }}
                  defaultValue={""}
                  placeholder={t("plz_chs")}
                  options={localeList}
                  className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                  onChange={(event) => {
                    field.onChange(event.value);
                  }}
                />
              )}
            />
            {getFormErrorMessage(errors, "locale")}
          </div>
          {/* 會員等級 */}
          <div className="col-12 lg:col-6">
            <label className="form-label">{t("membershipLevel")}</label>
            <span>{companyInfo?.companyLevelName}</span>
          </div>
          {/* 檔案使用情況 */}
          <div className="col-12 lg:col-6">
            <label className="form-label">{t("storageUsage")}</label>
            <StorageProgressBar used={companyInfo?.useS3Bucket} total={companyInfo?.s3BucketQuota} />
          </div>
          {/* 公司沿革 */}
          <div className="col-12">
            <FormTextarea
              disabled={readonly || !isEditable}
              register={register}
              errors={errors}
              id="history"
              name="history"
              type="text"
              labelText={t("history")}
              areaClass="w-full"
              placeholder={t("validation.limitMaxChar", { max: 500 })}
            />
          </div>
          {/* 主要營業項目與產品應用 */}
          <div className="col-12">
            <FormTextarea
              disabled={readonly || !isEditable}
              register={register}
              errors={errors}
              id="business"
              name="business"
              type="text"
              labelText={t("business")}
              areaClass="w-full"
              placeholder={t("validation.limitMaxChar", { max: 500 })}
            />
          </div>
          {/* 政策聲明 */}
          <div className="col-12">
            <FormTextarea
              disabled={readonly || !isEditable}
              register={register}
              errors={errors}
              id="policy"
              name="policy"
              type="text"
              labelText={t("policy")}
              areaClass="w-full"
              placeholder={t("validation.limitMaxChar", { max: 500 })}
            />
          </div>
          {/* 地點輸入區塊  以下暫時不要 */}
          {/* <hr />
          <div className="col-12 lg:col-6">
          
          <h3>{t('place')}</h3>
          {
            readonly === true && (
              <ul>
                {
                  placeFields?.map((item,index)=>{
                    return <li key={index}>{item?.placeName}</li>
                  })
                }
              </ul>
            )   
          }
          {
            readonly === false && (
                placeFields?.map((Field,index)=>{
                  return (
                    <div key={Field.id} className="formgrid grid">
                      <div className="field">
                        <FormInput
                          disabled={ Field?.isUsed==='N'?false:true }
                          register={register}
                          errors={errors}
                          id={`placeList.${index}.placeName`}
                          withLabel={false}
                          rules={{
                            required:{
                              value:true,
                              message:t('placeName_null_repeat')
                            }
                          }}
                          type="text"/>   
                      </div>
                       
                      {
                        Field?.isUsed==='N' && (

                          <div className="field">
                          <Tooltip title={t('button.delete')}>
                            <button type="button" className="btn border-0"
                              onClick={() => { placeFields?.length === 1 ? handleResetForm() : delForm(index) }}>
                              <FontAwesomeIcon icon="fa-solid fa-circle-minus" style={{ fontSize: '20px' }} className="icon-main-color" />
                            </button>
                          </Tooltip>
  
                        </div> 

                        )
                      }
                      {
                        Field?.isUsed==='Y' && (

                          <div className="field">
                          <Tooltip title={t('place_isUsed_hint')}>
                            <button type="button" className="btn border-0">
                              <FontAwesomeIcon icon="fa-solid fa-circle-question" style={{ fontSize: '20px' }} className="icon-main-color" />
                            </button>
                          </Tooltip>
  
                        </div> 

                        )
                      }
                        
                    
                    </div>
                  )
                })

              
            )
          }
            <div className="form-group" style={{display:readonly===true?'none':''}}>                  
              <Tooltip title={t('button.add')}>
                <button onClick={()=>{
                  addForm({placeName:'',isUsed:'N'});
                  reset({
                    placeList:getValues("placeList")
                  })
                  }}  type="button" className="btn border-0">
                  <FontAwesomeIcon icon="fa-solid fa-circle-plus" style={{ fontSize: '20px' }} className="icon-main-color" />
                </button> 
              </Tooltip>
            </div> 
          </div> */}

          <div className="col-12 gap-2 flex-all-center">
            {readonly === true ? (
              <>
                {getValues("companySeq") !== undefined && canDownloadAuthorizationLetter() && (
                  <Button
                    disabled={getValues("companySeq") === undefined}
                    label={t("button.downloadAuthorizationLetter")}
                    onClick={(e) => {
                      e.preventDefault();
                      handleDownloadAuthorizationLetter();
                    }}
                    size="small"
                    outlined
                  />
                )}
                <Button
                  disabled={getValues("companySeq") === undefined || !isEditable}
                  label={t("button.edit")}
                  onClick={(e) => {
                    e.preventDefault();
                    setReadonly(false);
                    setMaskView(true);
                    axios_updateAuditEyesEvent();
                  }}
                  size="small"
                />
              </>
            ) : (
              <>
                <Button disabled={!isValid || !isEditable} type="submit" label={t("button.confirm")} size="small" />
                <Button
                  outlined
                  size="small"
                  label={t("button.cancel")}
                  onClick={(e) => {
                    e.preventDefault();
                    handleBtnCancel();
                    setReadonly(true);
                    setMaskView(false);
                    clearErrors();
                  }}
                />
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompanyDataSetting;
